import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { GetQueryResultQuery, GetQueryResultQueryVariables, QueryResultInput } from '../../../../../../API';
import { GetQueryResult } from '../../../../../../graphql/queries';
import { ApiService } from '../../../../../shared/services/api.service';
import { ChartDataService } from '../../../../../market-insights/services/chart-data.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { QueryResultColumn, WorkbenchResultQueryRecord } from '../../../../models/history-model';

@Component({
  selector: 'app-workbench-query-result',
  templateUrl: './workbench-query-result.component.html',
  styleUrl: './workbench-query-result.component.scss',
})
export class WorkbenchQueryResultComponent implements OnChanges {
  @Input() queryRecord: WorkbenchResultQueryRecord | null = null;
  @Input() loadToChartData = false;

  isLoading = false;
  searchValue = '';
  totalCount = 0;
  pageIndex = 1;
  pageSize = 10;
  headers: QueryResultColumn[] = [];
  rawData: Record<string, unknown>[] = [];
  rows: Record<string, unknown>[] = [];
  metadata: any;
  readonly ALLOWED_MAX_RECORDS_FOR_SEARCH = 20_00_000; //2M
  readonly LIMITING_SEARCH_MESSAGE = `Search is available for results up to 2 million records. This query returns more than 2 million records. Please add filters or refine your search criteria to reduce the result set and try again.`;

  private lastFetchKey = '';
  private hasMoreResults = true;
  private backendPageNumber = 1;
  private readonly backendPageSize = 2000;
  private readonly maxChartLoadRows = 30000;
  private activeRecordKey = '';
  private lastTriggeredLoadedPages = 0;

  constructor(
    private apiService: ApiService,
    private chartDataService: ChartDataService,
    private messageService: NzMessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['queryRecord']) {
      const record = this.queryRecord;
      const nextRecordKey = record
        ? [record.orgId, record.queryId, record.executionId, record.athenaExecutionId].join(':')
        : '';

      if (!nextRecordKey || nextRecordKey === this.activeRecordKey) {
        return;
      }

      this.activeRecordKey = nextRecordKey;
      this.resetPaginationState();
      void this.fetchBackendPage();
    }
  }

  reset(): void {
    this.searchValue = '';
    this.search();
  }

  onPageChange(pageIndex: number): void {
    if (!this.queryRecord || pageIndex < 1 || this.isLoading) {
      return;
    }

    if (pageIndex === this.pageIndex) {
      return;
    }

    this.pageIndex = pageIndex;
    const loadedPages = this.getLoadedPageCount();

    if (pageIndex === loadedPages && this.hasMoreResults && loadedPages > this.lastTriggeredLoadedPages) {
      this.lastTriggeredLoadedPages = loadedPages;
      void this.fetchBackendPage();
      return;
    }

    this.rows = this.buildPageRows();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.pageIndex = 1;
    this.rows = this.buildPageRows();
  }

  search(): void {
    this.resetPaginationState();
    void this.fetchBackendPage();
  }

  getValue(data: Record<string, unknown>, key: string): string {
    const value = data[key] ?? '--';
    return String(value);
  }

  private async fetchBackendPage(): Promise<void> {
    const record = this.queryRecord;

    if (!record || !record.orgId || !record.queryId || !record.executionId || !record.athenaExecutionId) {
      this.clearTable();
      return;
    }
    const searchStr = this.searchValue.trim().toLowerCase();

    const fetchKey = [
      record.orgId,
      record.queryId,
      record.executionId,
      record.athenaExecutionId,
      this.backendPageNumber,
      searchStr,
    ].join(':');

    if (this.lastFetchKey === fetchKey) {
      return;
    }

    this.lastFetchKey = fetchKey;
    this.isLoading = true;

    const input: QueryResultInput = {
      orgId: record.orgId,
      queryId: record.queryId,
      executionId: record.executionId,
      athenaExecutionId: record.athenaExecutionId,
      maxRows: this.backendPageSize,
      pageNumber: this.backendPageNumber,
      searchStr: searchStr,
    };

    try {
      const response = await this.apiService.query<GetQueryResultQuery, GetQueryResultQueryVariables>(GetQueryResult, {
        input,
      });

      const resultData = response.data?.GetQueryResult?.data;
      const headerKeys = resultData?.headers ?? [];
      const parsedRows = this.parseRows(resultData?.rows);
      const backendPageSize = resultData?.pagination?.pageSize ?? this.backendPageSize;
      const backendPageNumber = resultData?.pagination?.pageNumber ?? this.backendPageNumber;

      if (this.headers.length === 0) {
        this.headers = this.buildHeaders(headerKeys);
      }

      this.rawData = [...this.rawData, ...parsedRows];
      this.rows = this.buildPageRows();
      this.totalCount = this.rawData.length;

      if (this.loadToChartData) {
        this.chartDataService.setData(this.rawData);
      }

      this.hasMoreResults = parsedRows.length === backendPageSize && this.rawData.length < this.maxChartLoadRows;
      this.backendPageNumber = backendPageNumber + 1;

      this.shouldContinueLoadingMoreResults(parsedRows.length, backendPageSize);
    } catch {
      this.messageService.error(`Failed to fetch search results, Try resetting the search.`);
    } finally {
      this.isLoading = false;
    }
  }

  private async shouldContinueLoadingMoreResults(parsedRowsLength: number, backendPageSize: number): Promise<void> {
    if (!this.loadToChartData || !this.hasMoreResults || parsedRowsLength === 0 || parsedRowsLength < backendPageSize) {
      return;
    }

    if (this.rawData.length >= this.maxChartLoadRows) {
      this.hasMoreResults = false;
      return;
    }

    await this.fetchBackendPage();
  }

  private clearTable(): void {
    this.headers = [];
    this.rawData = [];
    this.rows = [];
    this.totalCount = 0;
    this.pageIndex = 1;
    this.hasMoreResults = true;
    this.backendPageNumber = 1;
    this.lastTriggeredLoadedPages = 0;
  }

  private getLoadedPageCount(): number {
    return Math.max(1, Math.ceil(this.rawData.length / this.pageSize));
  }

  private buildPageRows(): Record<string, unknown>[] {
    const startIndex = (this.pageIndex - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.rawData.slice(startIndex, endIndex);
  }

  private parseRows(rows: string | null | undefined): Record<string, unknown>[] {
    if (!rows) {
      return [];
    }

    try {
      const parsed = JSON.parse(rows) as unknown;

      return Array.isArray(parsed) ? (parsed as Record<string, unknown>[]) : [];
    } catch {
      return [];
    }
  }

  private resetPaginationState(): void {
    this.clearTable();
    this.pageSize = 10;
    this.pageIndex = 1;
    this.backendPageNumber = 1;
    this.hasMoreResults = true;
    this.lastFetchKey = '';
  }

  private buildHeaders(keys: string[]): QueryResultColumn[] {
    return keys.map((key) => ({
      key,
      label: key,
      sortOrder: null,
      sortFn: (a: Record<string, unknown>, b: Record<string, unknown>) => this.compareColumnValue(a[key], b[key]),
      sortDirections: ['ascend', 'descend', null],
    }));
  }

  private compareColumnValue(left: unknown, right: unknown): number {
    if (typeof left === 'number' && typeof right === 'number') {
      return left - right;
    }

    return String(left ?? '').localeCompare(String(right ?? ''), undefined, { sensitivity: 'base' });
  }

  private getMetadata(): any {
    if (this.metadata) return this.metadata;
    try {
      this.metadata = JSON.parse(this.queryRecord?.metadata || '{}');
      return this.metadata;
    } catch (e) {
      console.log('error parsing metadata', e);
    }
    return {};
  }

  get isLimitingSearch(): boolean {
    const metadata = this.getMetadata();
    return metadata['totalRecords'] > this.ALLOWED_MAX_RECORDS_FOR_SEARCH;
  }
}
